import React, { useState, useEffect } from 'react';
import {
  Table, Input, message, DatePicker, Button, Typography,
  Popconfirm, Card, Space, Avatar, Badge, Statistic, Row, Col,
  Tooltip, Pagination
} from 'antd';
import {
  SearchOutlined, ReloadOutlined, DeleteOutlined, UserOutlined,
  MailOutlined, PhoneOutlined, CalendarOutlined, TeamOutlined,
  ClearOutlined, EyeOutlined, InboxOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import API from '../../API/API';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

/* ─── Quick-filter presets ─────────────────────────────────────────────────── */
const QUICK_FILTERS = [
  {
    key: 'today',
    label: 'Today',
    range: () => [dayjs().startOf('day'), dayjs().endOf('day')],
  },
  {
    key: 'yesterday',
    label: 'Yesterday',
    range: () => [
      dayjs().subtract(1, 'day').startOf('day'),
      dayjs().subtract(1, 'day').endOf('day'),
    ],
  },
  {
    key: 'this_week',
    label: 'This Week',
    range: () => [dayjs().startOf('week'), dayjs().endOf('week')],
  },
  {
    key: 'last_week',
    label: 'Last Week',
    range: () => [
      dayjs().subtract(1, 'week').startOf('week'),
      dayjs().subtract(1, 'week').endOf('week'),
    ],
  },
  {
    key: 'this_month',
    label: 'This Month',
    range: () => [dayjs().startOf('month'), dayjs().endOf('month')],
  },
  {
    key: 'last_month',
    label: 'Last Month',
    range: () => [
      dayjs().subtract(1, 'month').startOf('month'),
      dayjs().subtract(1, 'month').endOf('month'),
    ],
  },
  {
    key: 'last_3_months',
    label: 'Last 3 Months',
    range: () => [
      dayjs().subtract(3, 'month').startOf('month'),
      dayjs().endOf('month'),
    ],
  },
];

const Customer = () => {
  const [customer, setCustomer] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateRange, setDateRange] = useState([dayjs().startOf('month'), dayjs().endOf('month')]);
  const [activeQuickFilter, setActiveQuickFilter] = useState('this_month');
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(30);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loginUser, setLoginUser] = useState(null);
  const [stats, setStats] = useState({ total: 0, withEmail: 0, withPhone: 0, newThisMonth: 0 });

  const navigate = useNavigate();
  const isAdmin = loginUser?.role === 'ADMIN';

  useEffect(() => {
    const userData = localStorage.getItem('userData');
    if (!userData) navigate('/login');
    else setLoginUser(JSON.parse(userData));
  }, []);

  const calculateStats = (data) => {
    const startOfMonth = dayjs().startOf('month');
    const s = data.reduce(
      (acc, c) => {
        acc.total++;
        if (c.email) acc.withEmail++;
        if (c.phoneNumber) acc.withPhone++;
        if (c.createdAt && dayjs(c.createdAt).isAfter(startOfMonth)) acc.newThisMonth++;
        return acc;
      },
      { total: 0, withEmail: 0, withPhone: 0, newThisMonth: 0 }
    );
    setStats(s);
  };

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      if (!Array.isArray(dateRange) || dateRange.length !== 2 || !dateRange[0] || !dateRange[1]) return;
      const params = {
        startDate: dateRange[0].format('YYYY-MM-DD'),
        endDate: dateRange[1].format('YYYY-MM-DD'),
        page: currentPage,
        limit: pageSize,
      };
      if (searchTerm?.trim()) params.search = searchTerm.trim();

      const response = await API.GetAllCustomer(params);
      if (response?.status) {
        let data = response?.data || [];
        if (!isAdmin && loginUser?.id) {
          data = data.filter((item) => item.handledBy?._id === loginUser?.id);
        }
        setCustomer(data);
        setTotalRecords(response.pagination.totalRecords);
        calculateStats(data);
      } else {
        setCustomer([]);
      }
    } catch (error) {
      console.error('Failed to fetch customers:', error);
      setCustomer([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(fetchCustomers, 500);
    return () => clearTimeout(t);
  }, [currentPage, pageSize, dateRange, searchTerm, loginUser]);

  const handleTableChange = (pagination) => {
    setCurrentPage(pagination.current);
    setPageSize(pagination.pageSize);
  };

  const applyQuickFilter = (filterKey) => {
    const filter = QUICK_FILTERS.find((f) => f.key === filterKey);
    if (filter) {
      setDateRange(filter.range());
      setActiveQuickFilter(filterKey);
      setCurrentPage(1);
    }
  };

  const handleCustomRange = (range) => {
    setDateRange(range);
    setActiveQuickFilter(null); // deselect quick filters when custom range chosen
    setCurrentPage(1);
  };

  const deleteSelected = async () => {
    if (!selectedRowKeys.length) { message.warning('No rows selected.'); return; }
    try {
      const response = await API.DeleteCustomer(selectedRowKeys);
      if (response.status) {
        setCustomer((prev) => prev.filter((item) => !selectedRowKeys.includes(item._id)));
        setSelectedRowKeys([]);
        message.success(`${selectedRowKeys.length} record(s) deleted successfully!`);
      } else {
        message.error(response.message || 'Failed to delete selected records.');
      }
    } catch {
      message.error('Failed to delete selected records.');
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    applyQuickFilter('this_month');
    setCurrentPage(1);
    message.info('Filters cleared');
  };

  const getAgentName = (record) => {
    const agent = record.handledBy;
    if (!agent) return 'Unassigned';
    if (agent.firstName && agent.lastName) return `${agent.firstName} ${agent.lastName}`;
    if (agent.name) return agent.name;
    return 'Unassigned';
  };

  const rowSelection = isAdmin
    ? { selectedRowKeys, onChange: (keys) => setSelectedRowKeys(keys) }
    : undefined;

  const columns = [
    ...(isAdmin
      ? [
          {
            title: 'Sales-Agent',
            key: 'agent',
            width: 160,
            render: (_, record) => {
              const agentName = getAgentName(record);
              const isUnassigned = agentName === 'Unassigned';
              return (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Avatar
                    size={30}
                    style={{
                      background: isUnassigned ? '#e5e7eb' : '#0f172a',
                      fontSize: 12,
                      fontFamily: 'Sora, sans-serif',
                      flexShrink: 0,
                    }}
                  >
                    {isUnassigned ? '?' : agentName?.[0]?.toUpperCase()}
                  </Avatar>
                  <Text
                    style={{
                      fontSize: 13,
                      color: isUnassigned ? '#9ca3af' : '#1e293b',
                      fontStyle: isUnassigned ? 'italic' : 'normal',
                      fontFamily: 'Sora, sans-serif',
                    }}
                  >
                    {agentName}
                  </Text>
                </div>
              );
            },
          },
        ]
      : []),
    {
      title: 'Customer',
      key: 'customer',
      width: 270,
      render: (_, record) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{
            width: 40, height: 40, borderRadius: 12,
            background: 'linear-gradient(135deg, #0ea5e9 0%, #6366f1 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            <UserOutlined style={{ color: '#fff', fontSize: 16 }} />
          </div>
          <div>
            <Text strong style={{ fontSize: 14, fontFamily: 'Sora, sans-serif', color: '#0f172a', display: 'block' }}>
              {record.fullName || 'N/A'}
            </Text>
            <Text style={{ fontSize: 11, color: '#94a3b8', fontFamily: 'Sora, sans-serif' }}>
              ID: {record._id?.slice(-6)?.toUpperCase()}
            </Text>
          </div>
        </div>
      ),
    },
    {
      title: 'Contact',
      key: 'contact',
      width: 220,
      render: (_, record) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {record.email ? (
            <a
              href={`mailto:${record.email}`}
              data-no-row-click="true"
              onClick={(e) => e.stopPropagation()}
              style={{ fontSize: 12, color: '#0ea5e9', fontFamily: 'Sora, sans-serif', display: 'flex', alignItems: 'center', gap: 5 }}
            >
              <MailOutlined style={{ fontSize: 11 }} />
              {record.email.length > 24 ? `${record.email.substring(0, 24)}…` : record.email}
            </a>
          ) : <Text style={{ fontSize: 12, color: '#cbd5e1', fontFamily: 'Sora, sans-serif' }}>— no email</Text>}
          {record.phoneNumber ? (
            <a
              href={`tel:${record.phoneNumber}`}
              data-no-row-click="true"
              onClick={(e) => e.stopPropagation()}
              style={{ fontSize: 12, color: '#10b981', fontFamily: 'Sora, sans-serif', display: 'flex', alignItems: 'center', gap: 5 }}
            >
              <PhoneOutlined style={{ fontSize: 11 }} />
              {record.phoneNumber}
            </a>
          ) : <Text style={{ fontSize: 12, color: '#cbd5e1', fontFamily: 'Sora, sans-serif' }}>— no phone</Text>}
        </div>
      ),
    },
{
  title: 'Added',
  dataIndex: 'createdAt',
  key: 'createdAt',
  width: 180,
  render: (date) => (
    <Tooltip title={date ? dayjs(date).format('MM-DD-YYYY hh:mm A') : 'N/A'}>
      <div style={{ textAlign: 'left', lineHeight: '1.2' }}>
        
        {/* Date + Time */}
        <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>
          {date ? dayjs(date).format('MM/DD/YY - hh:mm A') : '—'}
        </div>

        {/* Relative time */}
        <div style={{ fontSize: 12, color: '#94a3b8' }}>
          {date ? `Created ${dayjs(date).fromNow()}` : ''}
        </div>

      </div>
    </Tooltip>
  ),
},
    {
      title: '',
      key: 'actions',
      width: 60,
      fixed: 'right',
      align: 'center',
      render: (_, record) => (
        <Tooltip title="View Details">
          <button
            data-no-row-click="true"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/customer-details/${record._id}`);
            }}
            style={{
              width: 34, height: 34, borderRadius: 10,
              background: '#f1f5f9', border: '1px solid #e2e8f0',
              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'all 0.18s',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = '#0f172a'; e.currentTarget.querySelector('span').style.color = '#fff'; }}
            onMouseLeave={e => { e.currentTarget.style.background = '#f1f5f9'; e.currentTarget.querySelector('span').style.color = '#64748b'; }}
          >
            <EyeOutlined style={{ fontSize: 14, color: '#64748b' }} />
          </button>
        </Tooltip>
      ),
    },
  ];

  const isSearching = searchTerm?.trim().length > 0;

  /* ── Stat card helper ── */
  const StatBox = ({ label, value, color }) => (
    <div style={{
      background: 'rgba(255,255,255,0.08)', borderRadius: 14,
      padding: '14px 18px', flex: 1, minWidth: 100,
      border: '1px solid rgba(255,255,255,0.12)',
    }}>
      <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.65)', fontFamily: 'Sora, sans-serif', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 700, color: color || '#fff', fontFamily: 'Sora, sans-serif', lineHeight: 1 }}>{value}</div>
    </div>
  );

  return (
    <div style={{ padding: 28, minHeight: '100vh', background: '#f8fafc', fontFamily: 'Sora, sans-serif' }}>

      {/* ── Google Font import ── */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700;800&display=swap');

        .cust-row:hover td { background: #f0f9ff !important; transition: background 0.15s; }
        .cust-row { cursor: pointer; }

        .ant-table-thead > tr > th {
          background: #f8fafc !important;
          font-weight: 700 !important;
          font-size: 11px !important;
          color: #94a3b8 !important;
          letter-spacing: 0.08em !important;
          border-bottom: 1px solid #e2e8f0 !important;
          font-family: 'Sora', sans-serif !important;
        }
        .ant-table-tbody > tr > td {
          border-bottom: 1px solid #f1f5f9 !important;
          font-family: 'Sora', sans-serif !important;
        }
        .ant-table-cell { padding: 14px 16px !important; }

        .qf-btn {
          border: 1.5px solid #e2e8f0;
          background: #fff;
          border-radius: 20px;
          padding: 4px 14px;
          font-size: 12px;
          font-weight: 500;
          color: #64748b;
          cursor: pointer;
          transition: all 0.16s;
          white-space: nowrap;
          font-family: 'Sora', sans-serif;
        }
        .qf-btn:hover { border-color: #0f172a; color: #0f172a; }
        .qf-btn.active {
          background: #0f172a;
          border-color: #0f172a;
          color: #fff;
        }
      `}</style>

      {/* ── Hero Header ── */}
      <motion.div initial={{ opacity: 0, y: -16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }}>
        <div style={{
          background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 60%, #0ea5e9 100%)',
          borderRadius: 24, padding: '28px 36px', marginBottom: 20,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 24, flexWrap: 'wrap',
        }}>
          {/* Title block */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 18 }}>
            <div style={{
              width: 58, height: 58, borderRadius: 18,
              background: 'rgba(255,255,255,0.12)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              border: '1px solid rgba(255,255,255,0.18)',
            }}>
              <TeamOutlined style={{ fontSize: 28, color: '#fff' }} />
            </div>
            <div>
              <div style={{ fontSize: 22, fontWeight: 800, color: '#fff', letterSpacing: '-0.02em' }}>
                Customer Management
              </div>
              <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.6)', marginTop: 2 }}>
                Track and manage your customer relationships
              </div>
            </div>
          </div>

          {/* Stat boxes */}
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <StatBox label="Total Shown" value={stats.total} />
            <StatBox label="With Email" value={stats.withEmail} color="#34d399" />
            <StatBox label="New This Month" value={stats.newThisMonth} color="#fbbf24" />
          </div>
        </div>
      </motion.div>

      {/* ── Filter Bar ── */}
      <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
        <div style={{
          background: '#fff', borderRadius: 18, padding: '18px 24px',
          marginBottom: 16, border: '1px solid #e2e8f0',
          boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
        }}>
          {/* Row 1: Search + Date range picker + Actions */}
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
            <Input
              placeholder="Search name, email, phone…"
              prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              allowClear
              style={{
                flex: '1 1 220px', borderRadius: 12, height: 40,
                fontFamily: 'Sora, sans-serif', fontSize: 13,
              }}
            />
            <RangePicker
              value={dateRange}
              onChange={handleCustomRange}
              disabledDate={(d) => d && d > dayjs().endOf('day')}
              style={{ borderRadius: 12, height: 40, fontFamily: 'Sora, sans-serif' }}
              placeholder={['Start Date', 'End Date']}
            />
            <Tooltip title="Refresh">
              <Button
                icon={<ReloadOutlined />}
                onClick={fetchCustomers}
                loading={loading}
                style={{ borderRadius: 12, height: 40, width: 40, padding: 0 }}
              />
            </Tooltip>
            <Tooltip title="Clear Filters">
              <Button
                icon={<ClearOutlined />}
                onClick={clearFilters}
                style={{ borderRadius: 12, height: 40, width: 40, padding: 0 }}
              />
            </Tooltip>
            {isAdmin && selectedRowKeys.length > 0 && (
              <Popconfirm
                title="Delete selected customers?"
                description={`Delete ${selectedRowKeys.length} customer(s)? This cannot be undone.`}
                onConfirm={deleteSelected}
                okText="Delete"
                cancelText="Cancel"
                okType="danger"
              >
                <Button
                  danger
                  icon={<DeleteOutlined />}
                  style={{ borderRadius: 12, height: 40, fontFamily: 'Sora, sans-serif' }}
                >
                  Delete ({selectedRowKeys.length})
                </Button>
              </Popconfirm>
            )}
          </div>

          {/* Row 2: Quick filter chips */}
          <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', letterSpacing: '0.06em', marginRight: 4 }}>
              QUICK FILTER
            </span>
            {QUICK_FILTERS.map((f) => (
              <button
                key={f.key}
                className={`qf-btn${activeQuickFilter === f.key ? ' active' : ''}`}
                onClick={() => applyQuickFilter(f.key)}
              >
                {f.label}
              </button>
            ))}

            {/* Range summary badge */}
            <div style={{
              marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6,
              background: '#f0f9ff', border: '1px solid #bae6fd',
              borderRadius: 20, padding: '4px 12px',
            }}>
              <CalendarOutlined style={{ color: '#0ea5e9', fontSize: 12 }} />
              <span style={{ fontSize: 12, color: '#0369a1', fontWeight: 500 }}>
                {dateRange[0]?.format('MMM D')} – {dateRange[1]?.format('MM-DD-YYYY')}
              </span>
              <span style={{
                background: '#0ea5e9', color: '#fff', borderRadius: 10,
                fontSize: 11, fontWeight: 700, padding: '1px 8px', marginLeft: 4,
              }}>
                {totalRecords.toLocaleString()}
              </span>
            </div>
          </div>
        </div>
      </motion.div>

      {/* ── Table Shell ── */}
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}>
        {/* Table header bar */}
        <div style={{
          background: '#fff', borderRadius: '16px 16px 0 0',
          padding: '14px 20px', borderBottom: '1px solid #f1f5f9',
          border: '1px solid #e2e8f0', 
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <InboxOutlined style={{ color: isSearching ? '#0ea5e9' : '#64748b', fontSize: 16 }} />
            <span style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>
              {isSearching ? 'Search Results' : 'Customer List'}
            </span>
            <span style={{
              background: '#f0fdf4', border: '1px solid #86efac',
              borderRadius: 10, fontSize: 12, fontWeight: 600,
              color: '#16a34a', padding: '1px 10px',
            }}>
              {customer.length} shown
            </span>
            <span style={{ fontSize: 12, color: '#94a3b8' }}>of {totalRecords.toLocaleString()} total</span>
          </div>
          <Pagination
            current={currentPage}
            pageSize={pageSize}
            total={totalRecords}
            showSizeChanger
            pageSizeOptions={['10', '20', '30', '50', '100']}
            onChange={(page, size) => { setCurrentPage(page); setPageSize(size); }}
            size="small"
            simple
          />
        </div>

        {/* Table */}
        <div style={{
          background: '#fff', borderRadius: '0 0 16px 16px',
          border: '1px solid #e2e8f0', borderTop: 'none',
          overflow: 'hidden',
        }}>
          <Table
            columns={columns}
            dataSource={customer}
            rowSelection={rowSelection}
            rowKey={(record) => record._id || record.key}
            loading={loading}
            pagination={false}
            onChange={handleTableChange}
            scroll={{ x: isAdmin ? 1100 : 900, y: 'calc(100vh - 520px)' }}
            size="middle"
            rowClassName={() => 'cust-row'}
            onRow={(record) => ({
              onClick: (event) => {
                const target = event.target;
                if (
                  target.closest('a') ||
                  target.closest('button') ||
                  target.closest('input') ||
                  target.closest('label') ||
                  target.closest('.ant-checkbox-wrapper') ||
                  target.closest('.ant-checkbox') ||
                  target.closest('[data-no-row-click="true"]')
                ) {
                  return;
                }

                navigate(`/customer-details/${record._id}`);
              }
            })}
            bordered={false}
          />

          {/* Bottom pagination */}
          {customer.length > 0 && (
            <div style={{
              display: 'flex', justifyContent: 'center',
              padding: '20px 24px', background: '#fafafa',
              borderTop: '1px solid #f1f5f9',
            }}>
              <Pagination
                current={currentPage}
                pageSize={pageSize}
                total={totalRecords}
                showSizeChanger
                pageSizeOptions={['10', '20', '30', '50', '100']}
                onChange={(page, size) => { setCurrentPage(page); setPageSize(size); }}
                showQuickJumper
                showTotal={(total, range) => (
                  <span style={{ fontFamily: 'Sora, sans-serif', fontSize: 13 }}>
                    {range[0]}–{range[1]} of {total} customers
                  </span>
                )}
              />
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default Customer;
